// v2.18.4 — Badge météo en overlay sur /livreur/navigate.
// Fetch /api/v3/courier/weather toutes les 10 min. Tooltip détaillé au hover/tap.

import { useEffect, useState, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const REFRESH_MS = 10 * 60 * 1000;

function getCurrentLatLng(fallback = [5.345, -4.024]) {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(fallback);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve([pos.coords.latitude, pos.coords.longitude]),
      () => resolve(fallback),
      { enableHighAccuracy: false, timeout: 6000, maximumAge: 60000 },
    );
  });
}

export default function WeatherBadge() {
  const [w, setW] = useState(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    async function tick() {
      try {
        const [lat, lng] = await getCurrentLatLng();
        const r = await axios.get(`${API}/v3/courier/weather?lat=${lat}&lng=${lng}`, { withCredentials: true });
        if (cancelled) return;
        if (r.data?.ok && r.data.weather) { setW(r.data.weather); setError(false); }
      } catch (_) {
        if (!cancelled) setError(true);
      }
    }
    tick();
    timerRef.current = setInterval(tick, REFRESH_MS);
    return () => { cancelled = true; if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  // Si 503 ou jamais reçu, on cache le badge silencieusement.
  if (error || !w) return null;

  return (
    <div data-testid="weather-badge" style={S.wrap} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)} onClick={() => setOpen((v) => !v)}>
      <span style={{ fontSize: 16, lineHeight: 1 }}>{w.icon_emoji}</span>
      <span style={S.temp}>{w.temp_c}°</span>
      {open && <span style={S.cond}>{w.condition}</span>}

      {open && (
        <div data-testid="weather-tooltip" style={S.tooltip}>
          <div style={{ fontWeight: 700, color: "#F1F5F9", marginBottom: 6 }}>
            {w.icon_emoji} {w.condition} · {w.city}
          </div>
          <div style={S.row}>Ressenti : <strong>{w.feels_like_c}°</strong></div>
          {w.wind_kmh != null && <div style={S.row}>Vent : <strong>{w.wind_kmh} km/h</strong></div>}
          {w.humidity != null && <div style={S.row}>Humidité : <strong>{w.humidity}%</strong></div>}
          {w.visibility_km != null && <div style={S.row}>Visibilité : <strong>{w.visibility_km} km</strong></div>}
          <div style={{ ...S.row, color: "#475569", marginTop: 6, fontSize: 9 }}>
            Mis à jour : {new Date(w.fetched_at).toLocaleTimeString("fr-FR")}
          </div>
        </div>
      )}
    </div>
  );
}

const S = {
  wrap: {
    // v2.19.34a — déplacé top-right (à gauche du burger 44px @right:12 → right:68 + 12 marge)
    position: "fixed", top: 12, right: 68, zIndex: 40,
    display: "flex", alignItems: "center", gap: 6,
    padding: "5px 11px",
    background: "rgba(15, 23, 42, 0.85)",
    border: "1px solid rgba(96, 165, 250, 0.35)",
    borderRadius: 999,
    backdropFilter: "blur(12px)",
    boxShadow: "0 6px 18px rgba(0,0,0,0.35)",
    cursor: "pointer",
    fontFamily: "Inter, system-ui, sans-serif",
    fontSize: 12,
  },
  temp: { color: "#F1F5F9", fontWeight: 700 },
  cond: { color: "#94A3B8", fontSize: 11, maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  tooltip: {
    // v2.19.34a — tooltip ancré à droite pour éviter dépassement écran (badge en top-right).
    position: "absolute", top: "100%", right: 0, marginTop: 6,
    minWidth: 200,
    padding: "10px 12px",
    background: "rgba(15, 23, 42, 0.97)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 10,
    boxShadow: "0 12px 32px rgba(0,0,0,0.55)",
    fontSize: 11,
    color: "#CBD5E1",
    pointerEvents: "none",
  },
  row: { padding: "2px 0" },
};
